/**
 * BotaoCopiar — copia um texto e CONFIRMA no próprio botão.
 *
 * Por que existe: um toast some da lateral da tela enquanto o olho de
 * quem clicou ainda está no botão. Confirmar onde a pessoa está olhando
 * elimina a dúvida "será que copiou?" — e o hábito de clicar de novo,
 * que é o sintoma dessa dúvida.
 *
 * Três cuidados que a aparência esconde:
 *
 * - A largura é a do MAIOR rótulo, sempre. Os dois ficam empilhados na
 *   mesma célula de grid e o inativo some por `visibility` — continua
 *   ocupando espaço. Reservar com um `min-width` fixo parece mais
 *   simples e não é: o valor depende da fonte, do tamanho do `rem` e do
 *   idioma, e erra em silêncio (aqui errou: 5.9rem virou 82,6px numa
 *   base de 14px, e "Copiado" precisa de 86).
 * - `navigator.clipboard` não existe fora de contexto seguro (HTTP puro)
 *   e pode ser negado por política. Falhar em silêncio dizendo
 *   "copiado" seria pior que não ter o botão; aqui a falha avisa.
 * - A escrita pode ficar PENDENTE para sempre — a API exige documento em
 *   foco, e sem foco a promessa não resolve nem rejeita. Visto na
 *   prática ao acionar o botão de uma aba em segundo plano. Sem um
 *   limite de espera o botão fica mudo: não confirma e não reclama.
 * - O timer é cancelado ao desmontar: sem isso, sair da tela nos dois
 *   segundos seguintes dispara `setState` em componente morto.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { FiCheck, FiCopy } from "react-icons/fi";

/** Quanto tempo a confirmação fica visível. */
const DURACAO_CONFIRMACAO_MS = 2000;

/** Além disso, a escrita é dada como perdida (ver cabeçalho). */
const LIMITE_ESCRITA_MS = 1500;

const surgir = keyframes`
  from { transform: scale(0.6); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
`;

const Botao = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 10px;
  border: 1px solid ${({ theme, $copiado }) => ($copiado ? theme.successBorder : theme.border)};
  border-radius: 7px;
  background: ${({ theme, $copiado }) => ($copiado ? theme.successSurface : "transparent")};
  color: ${({ theme, $copiado }) => ($copiado ? theme.successText : theme.textPrimary)};
  font-size: 12.5px;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease, border-color 140ms ease;

  &:hover { background: ${({ theme, $copiado }) => ($copiado ? theme.successSurface : theme.surfaceAlt || theme.background)}; }

  svg { animation: ${surgir} 180ms ease-out; }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    svg { animation: none; }
  }
`;

/* Os dois rótulos na MESMA célula: o maior define a largura, e o
   inativo desaparece sem devolver o espaço. */
const Rotulos = styled.span`
  display: grid;
  grid-template-areas: "rotulo";
  align-items: center;

  > span {
    grid-area: rotulo;
    white-space: nowrap;
  }
  > span[data-oculto="sim"] {
    visibility: hidden;
  }
`;

export default function BotaoCopiar({
  texto,
  rotulo = "Copiar",
  rotuloCopiado = "Copiado",
  onErro,
  ...resto
}) {
  const [copiado, setCopiado] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copiar = useCallback(async () => {
    try {
      if (!navigator.clipboard) {
        throw new Error("Área de transferência indisponível.");
      }
      await Promise.race([
        navigator.clipboard.writeText(texto),
        new Promise((_, rejeitar) =>
          setTimeout(
            () => rejeitar(new Error("A área de transferência não respondeu.")),
            LIMITE_ESCRITA_MS,
          ),
        ),
      ]);
      setCopiado(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopiado(false), DURACAO_CONFIRMACAO_MS);
    } catch (err) {
      // Quem chama decide como avisar (toast, mensagem inline). O que
      // não pode é o botão dizer "copiado" sem ter copiado.
      if (onErro) onErro(err);
    }
  }, [texto, onErro]);

  return (
    <Botao
      type="button"
      onClick={copiar}
      $copiado={copiado}
      aria-live="polite"
      {...resto}
    >
      {copiado ? <FiCheck /> : <FiCopy />}
      <Rotulos>
        <span data-oculto={copiado ? "sim" : "nao"}>{rotulo}</span>
        <span data-oculto={copiado ? "nao" : "sim"}>{rotuloCopiado}</span>
      </Rotulos>
    </Botao>
  );
}
